import React, { useState } from "react";
import { Check, Lock, Copy, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Welcome video: set to an https URL once provided. While null, the slot stays hidden.
const WELCOME_VIDEO_URL = null;

// ---------- First-login welcome guide (full screen, shown once) ----------
export function GuideOverlay({ firstName, onStart, onClose }) {
  const steps = [
    { n: 1, title: "Découvre ton business", time: "1 min" },
    { n: 2, title: "Fais ta Mission Jour 1", time: "15 min" },
    { n: 3, title: "Pose tes questions au coach IA", time: "quand tu veux" },
  ];
  return (
    <div className="guide-overlay" data-testid="guide-overlay" role="dialog" aria-modal="true">
      <div className="guide-card">
        <button className="guide-close" onClick={onClose} data-testid="guide-close" aria-label="Fermer le guide">×</button>
        <p className="eyebrow accent">BIENVENUE</p>
        <h2 className="guide-title" data-testid="guide-title">
          Bienvenue {firstName} 🚀 Ton business est prêt.<br />Voici comment ça marche en 3 étapes
        </h2>

        {WELCOME_VIDEO_URL && (
          <div className="guide-video" data-testid="guide-video">
            <video src={WELCOME_VIDEO_URL} controls playsInline preload="metadata" crossOrigin="anonymous">
              <track kind="captions" />
            </video>
          </div>
        )}

        <ol className="guide-steps" data-testid="guide-steps">
          {steps.map((s) => (
            <li key={s.n} className="guide-step">
              <span className="guide-step-num">{s.n}</span>
              <span className="guide-step-body">
                <span className="guide-step-title">{s.title}</span>
                <span className="guide-step-time"><Clock size={12} aria-hidden="true" /> {s.time}</span>
              </span>
            </li>
          ))}
        </ol>

        <button className="btn-primary w-full guide-cta" onClick={onStart} data-testid="guide-start">
          C'est parti 🚀
        </button>
      </div>
    </div>
  );
}

// ---------- Always-visible progress tracker ----------
export function ProgressTracker({ steps, percent, nextAction, onNext, onReplayGuide }) {
  return (
    <div className="progress-tracker" data-testid="progress-tracker">
      <div className="progress-tracker-head">
        <p className="field-label">Ta progression</p>
        <div className="progress-head-right">
          <span className="progress-percent" data-testid="progress-percent">{percent} %</span>
          <button className="progress-replay" onClick={onReplayGuide} data-testid="replay-guide">Revoir le guide</button>
        </div>
      </div>

      <div className="progress-steps" data-testid="progress-steps">
        {steps.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className={`progress-step ${s.done ? "done" : ""}`} data-testid={`progress-step-${s.key}`}>
              <span className="progress-step-dot">{s.done ? <Check size={13} aria-hidden="true" /> : i + 1}</span>
              <span className="progress-step-label">{s.label}{s.key === "client" ? " 🎯" : ""}</span>
            </div>
            {i < steps.length - 1 && <span className="progress-arrow" aria-hidden="true">→</span>}
          </React.Fragment>
        ))}
      </div>

      <div className="progress-bar-track" aria-hidden="true"><div className="progress-bar-fill" style={{ width: `${percent}%` }} /></div>

      {nextAction && nextAction.target !== "done" ? (
        <button className="btn-primary w-full progress-next" onClick={onNext} data-testid="progress-next-action">
          👉 Ma prochaine action : {nextAction.label}
        </button>
      ) : (
        <p className="progress-done-msg" data-testid="progress-all-done">🎉 Tu as tout fait. Continue sur ta lancée !</p>
      )}
    </div>
  );
}

// ---------- One mission, one action at a time (stepper) ----------
export function MissionStepper({ dayKey, mission, done, locked, onToggle, resultId, anchorId }) {
  const actions = (mission && mission.actions) || [];
  const firstNotDone = actions.findIndex((a) => !done[a.id]);
  const [idx, setIdx] = useState(firstNotDone < 0 ? 0 : firstNotDone);
  const allDone = actions.length > 0 && actions.every((a) => done[a.id]);

  if (locked) {
    return (
      <div className="mission-card locked" id={anchorId} data-testid={`mission-${dayKey}-locked`}>
        <span className="mission-lock" aria-hidden="true"><Lock size={18} /></span>
        <h3 className="mission-title">{mission?.title || "Mission Jour 2"}</h3>
        <p className="muted text-sm mt-2">Termine ta Mission Jour 1 pour débloquer celle-ci.</p>
      </div>
    );
  }
  if (!actions.length) return null;

  const a = actions[Math.min(idx, actions.length - 1)];
  const isDone = Boolean(done[a.id]);

  const copyTemplate = async () => {
    try {
      await navigator.clipboard.writeText(a.template_text || "");
      toast.success("Copié ✅");
    } catch (e) {
      toast.error("Copie impossible, sélectionne le texte manuellement.");
    }
    try { await api.post("/space/event", { type: "template_copied", label: a.template_label || a.title }); } catch (e) {}
  };

  const toggle = async () => {
    const next = !isDone;
    await onToggle(a.id, next);
    if (next) {
      toast.success("Bravo, une étape de plus vers ton premier client 💪");
      if (idx < actions.length - 1) setTimeout(() => setIdx(idx + 1), 350);
    }
  };

  return (
    <div className="mission-card" id={anchorId} data-testid={`mission-${dayKey}`}>
      <div className="mission-head">
        <div>
          <h3 className="mission-title">{mission.title}</h3>
          {mission.goal && <p className="muted text-sm mt-1">{mission.goal}</p>}
        </div>
        <span className="mission-progress" data-testid={`mission-${dayKey}-progress`}>
          {actions.filter((x) => done[x.id]).length}/{actions.length}
        </span>
      </div>

      {allDone ? (
        <div className="mission-complete" data-testid={`mission-${dayKey}-complete`}>
          <span className="mission-complete-badge"><Check size={18} aria-hidden="true" /></span>
          <p>Mission terminée, bravo ! Tu peux passer à la suite.</p>
        </div>
      ) : (
        <div className="action-view" data-testid={`action-${a.id}`}>
          <div className="action-topline">
            <span className="action-step-count mono">ÉTAPE {Math.min(idx, actions.length - 1) + 1} / {actions.length}</span>
            <span className="action-time"><Clock size={12} aria-hidden="true" /> {a.time || "15 min"}</span>
          </div>
          <h4 className="action-title">{a.title}</h4>

          <ol className="action-steps">
            {(a.steps || []).map((s, i) => <li key={i}>{s}</li>)}
          </ol>

          {a.example && (
            <div className="action-example"><span className="action-example-label">Exemple</span><p>{a.example}</p></div>
          )}

          {a.template_text && (
            <div className="action-template" data-testid={`template-${a.id}`}>
              <div className="action-template-head">
                <span className="action-template-label">{a.template_label || "Modèle à copier"}</span>
                <button className="action-copy" onClick={copyTemplate} data-testid={`copy-${a.id}`}>
                  <Copy size={14} aria-hidden="true" /> Copier
                </button>
              </div>
              <pre className="action-template-text">{a.template_text}</pre>
            </div>
          )}

          <label className="action-done-row" data-testid={`done-row-${a.id}`}>
            <span className={`action-done-box ${isDone ? "on" : ""}`}>{isDone && <Check size={14} aria-hidden="true" />}</span>
            <input type="checkbox" className="sr-only" checked={isDone} onChange={toggle} data-testid={`done-check-${a.id}`} />
            <span>Fait ✅</span>
          </label>

          <div className="action-nav">
            <button className="btn-secondary btn-small" disabled={idx === 0} onClick={() => setIdx(Math.max(0, idx - 1))} data-testid={`prev-${dayKey}`}>
              <ChevronLeft size={16} aria-hidden="true" /> Précédent
            </button>
            <button className="btn-secondary btn-small" disabled={idx >= actions.length - 1} onClick={() => setIdx(Math.min(actions.length - 1, idx + 1))} data-testid={`next-${dayKey}`}>
              Suivant <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- First client celebration card ----------
export function FirstClientCard({ done, onDone, anchorId }) {
  if (done) {
    return (
      <div className="first-client-card done" id={anchorId} data-testid="first-client-done">
        <h3>🎉 Félicitations, tu as décroché ton premier client !</h3>
        <p className="muted text-sm mt-2">C'est la preuve que ton business fonctionne. Continue sur ta lancée, le plus dur est derrière toi.</p>
      </div>
    );
  }
  return (
    <div className="first-client-card" id={anchorId} data-testid="first-client-card">
      <p className="eyebrow accent">OBJECTIF 🎯</p>
      <h3>Décroche ton premier client</h3>
      <p className="muted text-sm mt-2">Dès qu'une personne te dit oui, marque-le ici pour célébrer ta première victoire.</p>
      <button className="btn-primary mt-4" onClick={onDone} data-testid="first-client-btn">J'ai eu mon premier client !</button>
    </div>
  );
}

// ---------- Previous businesses (kept after a regeneration) ----------
export function PreviousBusinesses({ items }) {
  const [open, setOpen] = useState(null);
  if (!items || items.length === 0) return null;
  return (
    <div className="prev-biz" data-testid="previous-businesses">
      <p className="field-label">Mes business précédents</p>
      <div className="prev-biz-list mt-4">
        {items.map((it, i) => {
          const b = it.business || {};
          const isOpen = open === i;
          return (
            <div key={i} className="prev-biz-item" data-testid={`prev-biz-${i}`}>
              <button className="prev-biz-row" onClick={() => setOpen(isOpen ? null : i)} data-testid={`prev-biz-toggle-${i}`}>
                <div>
                  <p className="font-heading">{it.business_name}</p>
                  <p className="muted text-sm mt-1">{it.pitch}</p>
                </div>
                <span className="accent" aria-hidden="true">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div className="prev-biz-detail">
                  {b.target_client && <p className="text-sm"><b>Client cible :</b> {b.target_client}</p>}
                  {b.pricing && <p className="text-sm mt-2"><b>Prix :</b> {b.pricing}</p>}
                  {Array.isArray(b.plan_30_days) && b.plan_30_days.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-heading">Plan (aperçu)</p>
                      <ul className="prev-biz-plan">
                        {b.plan_30_days.slice(0, 3).map((d, j) => <li key={j}>Jour {d.day} — {d.title}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
